import { useTheme } from '@mui/material/styles';
import type { ColDef, ColGroupDef, RowClickedEvent } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import dayjs from 'dayjs';
import { useAtom, useAtomValue } from 'jotai';
import { type ReactElement, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { noDataRenderer } from '@/components/charts/charts.utils';
import RadialGaugeChart from '@/components/charts/RadialGaugeChart';
import VerticalBarChart from '@/components/charts/VerticalBarChart';
import GenericChartWidget from '@/components/GenericChartWidget';
import type { PercentageBarDataType } from '@/components/PercentageBarWidget';
import PercentageBarChart from '@/components/PercentageBarWidget/PercentageBarWidget';
import TableWidget from '@/components/TableWidget';
import { YEAR_MONTH_DAY_TIME_FORMAT } from '@/constants/date.constants';
import { useGridTheme } from '@/hooks/useGridTheme';
import { useFetchAnnouncements } from '@/pages/Announcements/Announcements.store';
import {
  useFetchEventsPerDay,
  useFetchUsage,
  useFetchUsageLimits
} from '../Dashboard.hooks';
import {
  casesEventsByDateAtom,
  casesPerDayAtom,
  dashboardTimeFilterAtom
} from '../Dashboard.store';
import useStyles from '../Dashboard.styles';
import {
  announcementsColumns,
  createTicketColumns,
  extractLimitMessage,
  formatUsageGaugeText
} from '../Dashboard.utils';
import { useFetchCaseMetrics } from '../DashboardCases/DashboardCases.store';
import { CASES_EVENTS_BAR_SERIES } from '../dashboard.constants';
import useDashboard from '../useDashboard.hooks';
import { SummarySmallCards } from './SummarySmallCards';

const NoRowsOverlay = () => {
  const theme = useTheme();
  return (
    <div
      style={{
        padding: '20px',
        textAlign: 'center',
        color: theme.vars.palette.text.secondary
      }}
    >
      No data
    </div>
  );
};

const DashboardSummary = () => {
  const {
    dashboardTimeFrame,
    usageAverages,
    logUsageLimit,
    networkUsageLimit,
    recentAnnouncements,
    orgProducts,
    selectedOrganization,
    tickets,
    percentageBarData,
    openTicketsCount,
    highPriorityTicketsCount,
    unresolvedTicketsCount,
    isFetchingGroupByOptions
  } = useDashboard();

  const { isFetching: isFetchingAnnouncements } = useFetchAnnouncements();
  const { isFetching: isFetchingLimits } = useFetchUsageLimits();
  const { isFetching: isFetchingEvents } = useFetchEventsPerDay();
  const { isFetching: isFetchingUsage } = useFetchUsage(orgProducts);
  const [, setCasesPerDay] = useAtom(casesPerDayAtom);

  const casesEventsData = useAtomValue(casesEventsByDateAtom);
  const timeFilter = useAtomValue(dashboardTimeFilterAtom);

  const theme = useTheme();

  const navigate = useNavigate();
  const { classes } = useStyles({
    logUsageLimit,
    networkUsageLimit,
    orgLabel: selectedOrganization.label
  });
  const gridTheme = useGridTheme();

  const fromDate = dayjs()
    .subtract(dashboardTimeFrame + 1, 'day')
    .startOf('day')
    .format(YEAR_MONTH_DAY_TIME_FORMAT);

  const toDate = dayjs()
    .subtract(1, 'day')
    .endOf('day')
    .format(YEAR_MONTH_DAY_TIME_FORMAT);

  const { isFetching: isFetchingCases } = useFetchCaseMetrics(
    { created_at: { from_date: fromDate, to_date: toDate }, group_by: 'day' },
    setCasesPerDay
  );

  const dashboardGridTheme = useMemo(
    () =>
      gridTheme.withParams({
        backgroundColor: theme.vars.palette.background.elevation2
      }),
    [gridTheme, theme]
  );

  /*
    When row is clicked, navigate to the ticket details page of ticket
  */
  const onRowClick = useCallback(
    (event: RowClickedEvent) => {
      navigate(`/ticket/${event.data.id}`);
    },
    [navigate]
  );

  // When bar clicked, navigate to the tickets page with the appropriate query
  const handleBarClick = useCallback(
    (value: string, filter: string, _data?: string) => {
      navigate(`/tickets?t=${timeFilter}&f={"${filter}":["__in","${value}"]}`);
    },
    [timeFilter]
  );

  const logPercent = logUsageLimit
    ? (usageAverages.log / logUsageLimit) * 100
    : 0;
  const networkPercent = networkUsageLimit
    ? (usageAverages.network / networkUsageLimit) * 100
    : 0;

  const announcementsTable = useMemo(() => {
    return (
      <TableWidget
        title="Recent Announcements"
        columns={announcementsColumns}
        rows={recentAnnouncements}
        loading={isFetchingAnnouncements}
        onRowClick={() => navigate('/announcements')}
      />
    );
  }, [announcementsColumns, isFetchingAnnouncements, recentAnnouncements]);

  const recentTicketsTable = useMemo(() => {
    return (
      <AgGridReact
        rowData={tickets || []}
        columnDefs={
          createTicketColumns('Recent Tickets') as (ColDef | ColGroupDef)[]
        }
        theme={dashboardGridTheme}
        rowHeight={50}
        noRowsOverlayComponent={NoRowsOverlay}
        className={classes.ticketsTable}
        overlayNoRowsTemplate={noDataRenderer()}
        onRowClicked={onRowClick}
      />
    );
  }, [tickets, dashboardGridTheme, onRowClick]);

  const ticketsByPriority = useMemo(() => {
    return (
      <GenericChartWidget
        title="Tickets by Priority"
        loading={isFetchingGroupByOptions}
      >
        <PercentageBarChart
          handleBarClick={(value) =>
            handleBarClick(value, 'ticket_priority__name')
          }
          data={percentageBarData as PercentageBarDataType[]}
        />
      </GenericChartWidget>
    );
  }, [handleBarClick, isFetchingGroupByOptions, percentageBarData]);

  const casesEventsTrendChart = useMemo(() => {
    return (
      <GenericChartWidget
        title={`${dashboardTimeFrame} Day Totals`}
        loading={isFetchingEvents || isFetchingCases}
      >
        <VerticalBarChart
          data={casesEventsData}
          series={CASES_EVENTS_BAR_SERIES}
        />
      </GenericChartWidget>
    );
  }, [dashboardTimeFrame, casesEventsData, isFetchingCases, isFetchingEvents]);

  const logPercentDescription = useMemo(() => {
    return formatUsageGaugeText(
      dashboardTimeFrame,
      usageAverages.log,
      logPercent,
      'log',
      logUsageLimit
    );
  }, [dashboardTimeFrame, logPercent, logUsageLimit, usageAverages.log]);

  const networkPercentDescription = useMemo(() => {
    return formatUsageGaugeText(
      dashboardTimeFrame,
      usageAverages.network,
      networkPercent,
      'network',
      networkUsageLimit
    );
  }, [
    dashboardTimeFrame,
    networkPercent,
    networkUsageLimit,
    usageAverages.network
  ]);

  const logLimitMessage = logPercentDescription
    ? extractLimitMessage(logPercentDescription as ReactElement, 'Log')
    : undefined;
  const networkLimitMessage = networkPercentDescription
    ? extractLimitMessage(networkPercentDescription as ReactElement, 'Network')
    : undefined;

  const stackedUsages = useMemo(() => {
    return (
      <>
        <GenericChartWidget loading={isFetchingUsage || isFetchingLimits}>
          <RadialGaugeChart
            description={logPercentDescription}
            limit={logUsageLimit || 0}
            limitMessage={logLimitMessage}
            orientation="horizontal"
            percent={parseFloat(usageAverages.log.toFixed(2))}
            primaryLabel={usageAverages.log.toFixed(2)}
            secondaryLabel="GB/Day"
            stackedDisplay
            title="Log Ingestion"
            onClick={() => navigate('/dashboard/usage')}
          />
        </GenericChartWidget>
        <GenericChartWidget loading={isFetchingUsage || isFetchingLimits}>
          <RadialGaugeChart
            description={networkPercentDescription}
            limit={networkUsageLimit || 0}
            limitMessage={networkLimitMessage}
            orientation="horizontal"
            percent={parseFloat(usageAverages.network.toFixed(2))}
            primaryLabel={usageAverages.network.toFixed(2)}
            secondaryLabel="Mbps"
            stackedDisplay
            title="Network Bandwidth Usage"
            onClick={() => navigate('/dashboard/usage')}
          />
        </GenericChartWidget>
      </>
    );
  }, [
    dashboardTimeFrame,
    isFetchingLimits,
    isFetchingUsage,
    logLimitMessage,
    logPercentDescription,
    logUsageLimit,
    networkLimitMessage,
    networkPercentDescription,
    networkUsageLimit,
    usageAverages
  ]);

  const logUsage = useMemo(() => {
    return (
      <GenericChartWidget loading={isFetchingUsage || isFetchingLimits}>
        <RadialGaugeChart
          description={logPercentDescription}
          limit={logUsageLimit || 0}
          limitMessage={logLimitMessage}
          orientation="vertical"
          percent={parseFloat(usageAverages.log.toFixed(2))}
          primaryLabel={usageAverages.log.toFixed(2)}
          secondaryLabel="GB/Day"
          title="Log Ingestion"
          onClick={() => navigate('/dashboard/usage')}
        />
      </GenericChartWidget>
    );
  }, [
    dashboardTimeFrame,
    isFetchingLimits,
    isFetchingUsage,
    logLimitMessage,
    logPercentDescription,
    logUsageLimit,
    usageAverages.log
  ]);

  const networkUsage = useMemo(() => {
    return (
      <GenericChartWidget loading={isFetchingUsage || isFetchingLimits}>
        <RadialGaugeChart
          description={networkPercentDescription}
          limit={networkUsageLimit || 0}
          limitMessage={networkLimitMessage}
          orientation="vertical"
          percent={parseFloat(usageAverages.network.toFixed(2))}
          primaryLabel={usageAverages.network.toFixed(2)}
          secondaryLabel="Mbps"
          title="Network Bandwidth Usage"
          onClick={() => navigate('/dashboard/usage')}
        />
      </GenericChartWidget>
    );
  }, [
    dashboardTimeFrame,
    isFetchingLimits,
    isFetchingUsage,
    networkLimitMessage,
    networkPercentDescription,
    networkUsageLimit,
    usageAverages.network
  ]);

  return (
    <div className={classes.dashboardPage}>
      <SummarySmallCards
        openTicketsCount={openTicketsCount}
        highPriorityTicketsCount={highPriorityTicketsCount}
        awaitingRemediationTicketsCount={unresolvedTicketsCount}
        selectedOrganization={selectedOrganization}
        timeFilter={timeFilter}
      />
      <div className={classes.dashboardMediumItem}>{ticketsByPriority}</div>
      <div className={classes.dashboardLargeItem}>{casesEventsTrendChart}</div>
      <div className={classes.dashboardRecentTickets}>{recentTicketsTable}</div>
      {selectedOrganization.label !== 'all' &&
        logUsageLimit !== null &&
        logUsageLimit > 0 &&
        networkUsageLimit !== null &&
        networkUsageLimit > 0 && (
          <div className={classes.dashboardStackedItem}>{stackedUsages}</div>
        )}
      {selectedOrganization.label !== 'all' &&
        logUsageLimit !== null &&
        logUsageLimit > 0 &&
        !networkUsageLimit && (
          <div className={classes.dashboardMediumItem}>{logUsage}</div>
        )}
      {selectedOrganization.label !== 'all' &&
        networkUsageLimit !== null &&
        networkUsageLimit > 0 &&
        !logUsageLimit && (
          <div className={classes.dashboardMediumItem}>{networkUsage}</div>
        )}
      <div className={classes.dashboardRecentTickets}>{announcementsTable}</div>
    </div>
  );
};

export default DashboardSummary;
